import type { ReactNode } from "react";
import { motion, useReducedMotion } from "motion/react";
import type { AgenticLogicSurface } from "@intelligems/ig-types/dtos/agentic-logic/surface";
import { IgChip } from "@intelligems/ig-theme/IgChip";
import { Box, Skeleton, Stack } from "@mui/material";
import { WorkspacePageFrame } from "@src/features/agenticCodegen/workspace/components/00-PageFrame/WorkspacePageFrame";
import { WorkspaceTitle } from "@src/features/agenticCodegen/workspace/components/00-PageFrame/WorkspaceTitle";
import { RuleDescriptionEditor } from "../rules/RuleDescriptionEditor";
import type { RuleListUsage } from "../rules/ruleListRows";
import { SURFACE_LABEL } from "../rules/surfaceLabels";
import { RuleMetaByline } from "./RuleMetaByline";
import type { RuleDetailTabKey } from "./ruleDetailTabs";
import { visibleRuleDetailTabs } from "./ruleDetailTabs";

/**
 * The surface chips beside the rule name. `null` is the degraded read — the
 * join that answers "where can this run" could not be loaded — and is kept
 * distinct from a rule that is eligible nowhere.
 */
const surfaceChipLabels = (
  eligibleSurfaces: AgenticLogicSurface[] | null
): string[] => {
  if (eligibleSurfaces === null) {
    return ["Unavailable"];
  }
  if (eligibleSurfaces.length === 0) {
    return ["None"];
  }
  return eligibleSurfaces.map((surface) => SURFACE_LABEL[surface]);
};

/** The header's surface chip: 3px / 8px padding around an 18px label. */
const HEADER_CHIP_SX = {
  height: "auto",
  px: "8px",
  py: "3px",
  "& .MuiChip-label": { px: 0, lineHeight: "18px" },
} as const;

const BuiltReveal = ({
  children,
  reveal,
  action = false,
  height = false,
}: {
  children: ReactNode;
  reveal: boolean;
  action?: boolean;
  height?: boolean;
}) => {
  const reducedMotion = useReducedMotion();
  return (
    <motion.div
      initial={
        reveal && !reducedMotion
          ? height
            ? { height: 7, opacity: 0 }
            : action
              ? { opacity: 0, scale: 0.96 }
              : { opacity: 0 }
          : false
      }
      animate={
        height
          ? { height: "auto", opacity: 1 }
          : action
            ? { opacity: 1, scale: 1 }
            : { opacity: 1 }
      }
      transition={
        reducedMotion || !reveal
          ? { duration: 0 }
          : { duration: 0.2, ease: [0, 0, 0.2, 1] }
      }
      style={height ? { overflow: "hidden" } : undefined}
    >
      {children}
    </motion.div>
  );
};

export interface RuleDetailPageViewProps {
  isBuilding?: boolean;
  revealBuilt?: boolean;
  name: string;
  description: string;
  eligibleSurfaces: AgenticLogicSurface[] | null;
  usedIn: RuleListUsage[] | null;
  isRenaming: boolean;
  isSavingDescription: boolean;
  selectedTab: RuleDetailTabKey;
  onSelectedTabChange: (tab: RuleDetailTabKey) => void;
  onRename: (name: string) => Promise<void> | void;
  onSaveDescription: (description: string) => Promise<void> | void;
  onBack: () => void;
  /**
   * Copy for the back button. Comes from the surface: a standalone rule page
   * returns to the rules list, while a builder embedded in an upsell returns
   * to the settings it took over.
   */
  backLabel: string;
  headerStatus?: ReactNode;
  /** The write actions, lifted out of the embedded workspace's title bar. */
  actions: ReactNode;
  /**
   * Whether the org may see the Advanced tab. Off withholds both the tab and
   * its body — the expert view is a per-org toggle, not a layout choice.
   */
  advancedEnabled: boolean;
  /** The embedded code (or template) workspace. */
  advanced: ReactNode;
  /** The rule-graph canvas (or its empty state) for the Selection logic tab. */
  selectionLogic: ReactNode;
  /** The checks strip directly below the main tabs, shared by every tab. */
  checks: ReactNode;
  /** The rule's recommendation-settings form, on the Display settings tab. */
  displaySettings: ReactNode;
  banners?: ReactNode;
}

/**
 * The rule detail page, as a pure view.
 *
 * Everything here is a prop so the layout can be exercised without a workspace
 * view model, a rule query, or a Monaco instance behind it — the container
 * above supplies all three.
 */
export const RuleDetailPageView = ({
  isBuilding = false,
  revealBuilt = false,
  name,
  description,
  eligibleSurfaces,
  usedIn,
  isRenaming,
  isSavingDescription,
  selectedTab,
  onSelectedTabChange,
  onRename,
  onSaveDescription,
  onBack,
  backLabel,
  headerStatus,
  actions,
  advancedEnabled,
  advanced,
  selectionLogic,
  checks,
  displaySettings,
  banners,
}: RuleDetailPageViewProps) => (
  <WorkspacePageFrame
    // The rule routes mount inside `AgenticLogicPageShell`, which already
    // provides the viewport-height elevated card.
    embedded
    backLabel={backLabel}
    onBack={onBack}
    headerStatus={headerStatus}
    banners={banners}
    title={
      isBuilding ? (
        <Skeleton
          aria-hidden
          data-testid="rule-header-skeleton"
          variant="text"
          width={240}
          sx={{ fontSize: 24 }}
        />
      ) : (
        <BuiltReveal reveal={revealBuilt}>
          <WorkspaceTitle
            name={name}
            onRename={onRename}
            saving={isRenaming}
            // Inside the title, so they sit 16px after the name rather than
            // behind the space the hidden rename button keeps.
            trailing={
              <Stack
                direction="row"
                alignItems="center"
                flexWrap="wrap"
                gap={1}
              >
                {surfaceChipLabels(eligibleSurfaces).map((label) => (
                  <IgChip
                    key={label}
                    label={label}
                    color="default"
                    sx={HEADER_CHIP_SX}
                  />
                ))}
              </Stack>
            }
          />
        </BuiltReveal>
      )
    }
    meta={
      isBuilding ? (
        <Skeleton aria-hidden variant="rounded" width={390} height={7} />
      ) : (
        <BuiltReveal reveal={revealBuilt}>
          <RuleMetaByline usedIn={usedIn} />
        </BuiltReveal>
      )
    }
    summary={
      isBuilding ? (
        <Skeleton aria-hidden variant="rounded" width={280} height={7} />
      ) : (
        <BuiltReveal reveal={revealBuilt} height>
          <RuleDescriptionEditor
            chrome="inline"
            description={description}
            saving={isSavingDescription}
            onSave={onSaveDescription}
          />
        </BuiltReveal>
      )
    }
    actions={
      isBuilding ? null : (
        <BuiltReveal reveal={revealBuilt} action>
          {actions}
        </BuiltReveal>
      )
    }
    tabs={visibleRuleDetailTabs(advancedEnabled)}
    selectedTab={selectedTab}
    onSelectedTabChange={onSelectedTabChange}
  >
    {checks ? <Box sx={{ px: 4 }}>{checks}</Box> : null}
    {/* Every body stays mounted and the inactive ones are hidden, rather than
        switching on `selectedTab`. The Advanced tab holds the editor buffer
        and any in-flight gate run; unmounting it to glance at the selection
        logic would drop an edit the autosave has not written yet. The others
        follow the same rule so one tab cannot behave differently from its
        neighbours — the canvas keeps its viewport across a round trip. */}
    <Box
      sx={{
        display: selectedTab === "selection_logic" ? "flex" : "none",
        flex: 1,
        flexDirection: "column",
        minHeight: 0,
      }}
    >
      {selectionLogic}
    </Box>
    <Box
      sx={{
        display: selectedTab === "display_settings" ? "flex" : "none",
        flex: 1,
        flexDirection: "column",
        minHeight: 0,
        overflow: "auto",
        // The canvas-slot padding from the design: 40px inline, 32px block.
        px: 10,
        py: 8,
      }}
    >
      {displaySettings}
    </Box>
    {advancedEnabled ? (
      <Box
        sx={{
          display: selectedTab === "advanced" ? "flex" : "none",
          flex: 1,
          flexDirection: "column",
          minHeight: 0,
          // Match the checks strip above so the editor and status card share
          // one left and right edge.
          px: 4,
        }}
      >
        {advanced}
      </Box>
    ) : null}
  </WorkspacePageFrame>
);
